import * as React from "react";
import _values from "lodash/values";
import merge from "lodash/merge";
import fill from "lodash/fill";
import find from "lodash/find";
import isEmpty from "lodash/isEmpty";
import styled, { useTheme, css } from "styled-components";
import { makeVisualization, retrieveChartData } from "views/components/aggregationbuilder/AggregationBuilder";
import HeatmapVisualizationConfig from "views/logic/aggregationbuilder/visualizations/HeatmapVisualizationConfig";
import useChartData from "views/components/visualizations/useChartData";
import useMapKeys from "views/components/visualizations/useMapKeys";
import usePlotOnClickPopover from "views/components/visualizations/hooks/usePlotOnClickPopover";
import heatmapOnClickPopover from "views/components/visualizations/heatmap/heatmapOnClickPopover";
import GenericPlot from "../GenericPlot";
const Container = styled.div(
  ({ $height, $width }) => css`
    height: ${$height ? `${$height}px` : "100%"};
    width: ${$width ? `${$width}px` : "100%"};
  `
);
const _generateSeriesTitles = (config, x, y) => {
  const seriesTitles = config.series.map((s) => s.function);
  const columnSeriesTitles = x.map((xLabel) => {
    if (seriesTitles.length > 1) {
      return seriesTitles.find((title) => xLabel.endsWith(title));
    }
    return seriesTitles.toString();
  });
  return y.map(() => columnSeriesTitles);
};
const _generateSeries = (visualizationConfig, mapKeys, theme) => ({ type, name, labels, values, data: z, config }) => {
  const rowPivots = config.rowPivots.flatMap((pivot) => pivot.fields);
  const columnPivots = config.columnPivots.flatMap((pivot) => pivot.fields);
  const xAxisTitle = rowPivots.join("-");
  const yAxisTitle = columnPivots.join("-");
  const zSeriesTitles = _generateSeriesTitles(config, values, labels);
  const hovertemplate = `${xAxisTitle}: %{y}<br>${yAxisTitle}: %{x}<br>%{text}: %{customdata}<extra></extra>`;
  const { colorScale, reverseScale, zMin, zMax } = visualizationConfig;
  const y = labels.map((value) => mapKeys(value, rowPivots[0]));
  const x = values.map((value) => mapKeys(value, columnPivots[0]));
  return {
    type,
    name,
    x,
    y,
    z,
    text: zSeriesTitles,
    customdata: z,
    hovertemplate,
    colorscale: colorScale,
    reversescale: reverseScale,
    zmin: zMin,
    zmax: zMax,
    originalName: name,
    colorbar: {
      tickfont: { color: theme.colors.text.primary }
    }
  };
};
const _fillUpMatrix = (z, xLabels, defaultValue = "None") => z.map((series) => {
  const newSeries = fill(Array(xLabels.length), defaultValue);
  return merge(newSeries, series);
});
const _transposeMatrix = (z = []) => {
  if (!z[0]) {
    return z;
  }
  return z[0].map((_, c) => z.map((r) => r[c]));
};
const _findSmallestValue = (valuesFound) => valuesFound.reduce(
  (result, valueArray) => valueArray.reduce((acc, value) => acc > value ? value : acc, result),
  (valuesFound[0] || [])[0]
);
const _formatSeries = (visualizationConfig) => ({ valuesBySeries, xLabels }) => {
  var _a;
  const valuesFoundBySeries = _values(valuesBySeries);
  const defaultValue = visualizationConfig.useSmallestAsDefault ? _findSmallestValue(valuesFoundBySeries) : (_a = visualizationConfig.defaultValue) != null ? _a : "None";
  const z = _transposeMatrix(_fillUpMatrix(valuesFoundBySeries, xLabels, defaultValue));
  const yLabels = Object.keys(valuesBySeries);
  return [["Heatmap Chart", xLabels, yLabels, z]];
};
const _axisConfig = (chartHasContent) => {
  const axisConfig = {
    type: void 0,
    fixedrange: true
  };
  if (chartHasContent) {
    axisConfig.type = "category";
  }
  return axisConfig;
};
const _chartLayout = (heatmapData) => {
  const hasContent = find(heatmapData, (series) => !isEmpty(series.z));
  const axisConfig = _axisConfig(hasContent);
  return {
    yaxis: axisConfig,
    xaxis: axisConfig,
    margin: {
      b: 40
    }
  };
};
const _leafSourceMatcher = ({ source }) => source.endsWith("leaf") && source !== "row-leaf";
const HeatmapVisualization = makeVisualization(({ config, data, height, width }) => {
  var _a;
  const theme = useTheme();
  const visualizationConfig = (_a = config.visualizationConfig) != null ? _a : HeatmapVisualizationConfig.empty();
  const rows = retrieveChartData(data);
  const mapKeys = useMapKeys();
  const heatmapData = useChartData(rows, {
    widgetConfig: config,
    chartType: "heatmap",
    generator: _generateSeries(visualizationConfig, mapKeys, theme),
    seriesFormatter: _formatSeries(visualizationConfig),
    leafValueMatcher: _leafSourceMatcher
  });
  const layout = _chartLayout(heatmapData);
  const { popover, initializeGraphDivRef, onChartClick } = usePlotOnClickPopover({ ...heatmapOnClickPopover, config });
  return /* @__PURE__ */ React.createElement(Container, { $height: height, $width: width }, /* @__PURE__ */ React.createElement(
    GenericPlot,
    {
      chartData: heatmapData,
      layout,
      onInitialized: initializeGraphDivRef,
      onClickMarker: onChartClick
    }
  ), popover);
}, "heatmap");
export default HeatmapVisualization;
